import * as dayjs from 'dayjs';
import { MapUtils } from '../../../utility/map-utils.js';
import { getFormattedName, getInitialsFromName } from '../../../utility/general-utility.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../../dto/dto.js';
import { PhotoReference } from '../file-reference/photo-reference.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { ICopyWithBusinessRef } from '../../../contracts/copy-with-business-ref.js';
import { QueryableOptionalString } from '../../core/optional.js';
import { TimeZone } from '../../../time/timezone.js';
import { Review } from '../../review.js';

export class OrderReview extends Review implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = '2e0b729955bd4965b8d920ec0a166229';
  static readonly SCHEMA_VERSION: number = 5;

  public readonly reviewerInitials;
  public readonly reviewerFormattedName;

  constructor(
    public readonly uniqueId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly businessRef: BusinessModuleRef,
    public readonly reviewText: string,
    public readonly reviewRating: number,
    public readonly reviewerId: string,
    public readonly reviewerFirstName: string,
    public readonly reviewerLastName: string,
    public readonly isAnonymous: boolean,
    public readonly reviewerPhoto: PhotoReference | null,

    // Used if the booking was made from a POI using BookNowComponent to link the
    // booking business to that POI.
    public readonly via: BusinessModuleRef | null,
    public readonly orderId: string,
    public readonly orderDate: dayjs.Dayjs,
    public readonly serviceId: string,
    public readonly serviceName: string,
    public readonly userId: string,
    public readonly staffMemberId: QueryableOptionalString,
  ) {
    super();
    this.reviewerInitials = getInitialsFromName(reviewerFirstName, reviewerLastName)!;
    this.reviewerFormattedName = getFormattedName(reviewerFirstName, reviewerLastName)!;
  }

  get $typeId(): string {
    return OrderReview.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OrderReview.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OrderReview {
    return new OrderReview(
        MapUtils.get<string>(map, 'uniqueId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.get<string>(map, 'reviewText'),
        MapUtils.get<number>(map, 'reviewRating'),
        MapUtils.get<string>(map, 'reviewerId'),
        MapUtils.get<string>(map, 'reviewerFirstName'),
        MapUtils.get<string>(map, 'reviewerLastName'),
        MapUtils.get<boolean>(map, 'isAnonymous'),
        context.fromDataOrNull<PhotoReference>(MapUtils.getOrNull<SerializedData>(map, 'reviewerPhoto')),
        BusinessModuleRef.decode(MapUtils.getOrNull<string>(map, 'via')),
        MapUtils.get<string>(map, 'orderId'),
        context.decodeDate(MapUtils.get<number>(map, 'orderDate')),
        MapUtils.get<string>(map, 'serviceId'),
        MapUtils.get<string>(map, 'serviceName'),
        MapUtils.get<string>(map, 'userId'),
        new QueryableOptionalString(MapUtils.get<string>(map, 'staffMemberId')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creationDate': context.encodeDate(this.creationDate),
      'businessRef': this.businessRef.encode(),
      'reviewText': this.reviewText,
      'reviewRating': this.reviewRating,
      'reviewerId': this.reviewerId,
      'reviewerFirstName': this.reviewerFirstName,
      'reviewerLastName': this.reviewerLastName,
      'isAnonymous': this.isAnonymous,
      'reviewerPhoto': context.toDataOrNull(this.reviewerPhoto),
      'via': this.via?.encode() ?? null,
      'orderId': this.orderId,
      'orderDate': context.encodeDate(this.orderDate),
      'serviceId': this.serviceId,
      'serviceName': this.serviceName,
      'userId': this.userId,
      'staffMemberId': this.staffMemberId.queryableValue,
      'reviewTextLength': this.reviewText.trim().length,
    };
  }

  clone(): this {
    return new OrderReview(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        this.businessRef.clone(),
        this.reviewText,
        this.reviewRating,
        this.reviewerId,
        this.reviewerFirstName,
        this.reviewerLastName,
        this.isAnonymous,
        this.reviewerPhoto?.clone() ?? null,
        this.via?.clone() ?? null,
        this.orderId,
        TimeZone.cloneDate(this.orderDate),
        this.serviceId,
        this.serviceName,
        this.userId,
        this.staffMemberId.clone(),
    ) as this;
  }

  isSame(other: OrderReview): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId == this.uniqueId &&
      other.creationDate == this.creationDate &&
      isSameData(other.businessRef, this.businessRef) &&
      other.reviewText == this.reviewText &&
      other.reviewRating == this.reviewRating &&
      other.reviewerId == this.reviewerId &&
      other.reviewerFirstName == this.reviewerFirstName &&
      other.reviewerLastName == this.reviewerLastName &&
      other.isAnonymous == this.isAnonymous &&
      isSameData(other.reviewerPhoto, this.reviewerPhoto) &&
      isSameData(other.via, this.via) &&
      other.orderId == this.orderId &&
      other.orderDate == this.orderDate &&
      other.serviceId == this.serviceId &&
      other.serviceName == this.serviceName &&
      other.userId == this.userId &&
      isSameData(other.staffMemberId, this.staffMemberId);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new OrderReview(
        this.uniqueId,
        TimeZone.cloneDate(this.creationDate),
        businessRef.clone(),
        this.reviewText,
        this.reviewRating,
        this.reviewerId,
        this.reviewerFirstName,
        this.reviewerLastName,
        this.isAnonymous,
        this.reviewerPhoto?.clone() ?? null,
        this.via?.clone() ?? null,
        this.orderId,
        TimeZone.cloneDate(this.orderDate),
        this.serviceId,
        this.serviceName,
        this.userId,
        this.staffMemberId.clone(),
    ) as this;
  }
}

export class OrderReviewBuilder extends DataModelBuilder<OrderReview> {
  build(context: SerializationContext, map: SerializedData): OrderReview {
    return OrderReview.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OrderReview.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(OrderReview.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the appointment date was added.
    if(version < 2) {
      const appointmentDate = context.decodeDate(MapUtils.get<number>(map, 'creationDate'));

      MapUtils.set(map, 'appointmentDate', context.encodeDate(appointmentDate));

      version = 2;
    }

    // In this version anonymous reviews have been introduced.
    if(version < 3) {
      MapUtils.set(map, 'isAnonymous', false);

      version = 3;
    }

    // In this version [businessId] was replaced with [businessRef]
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, 'businessRef', MapUtils.get(map, 'businessId'));

      version = 4;
    }

    // In this version the model was converted to `Review` and the following
    // changes were made:
    //  - model `Review` was renamed to `OrderReview`
    //  - parameter `appointmentId` was renamed to `orderId`
    //  - parameter `appointmentDate` was renamed to `orderDate`
    //  - parameter `clientId` was renamed to `reviewerId`
    //  - parameter `clientFirstName` was renamed to `reviewerFirstName`
    //  - parameter `clientLastName` was renamed to `reviewerLastName`
    //  - parameter `clientPhoto` was renamed to `reviewerPhoto`
    //  - parameter `likes` of type `number` was removed
    //  - parameter `dislikes` of type `number` was removed
    if(version < 5) {
      map['orderId'] ??= map['appointmentId'];
      map['orderDate'] ??= map['appointmentDate'];
      map['reviewerId'] ??= map['clientId'];
      map['reviewerFirstName'] ??= map['clientFirstName'];
      map['reviewerLastName'] ??= map['clientLastName'];
      map['reviewerPhoto'] ??= map['clientPhoto'];

      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }

  migrateFromCurrentSchemaVersion(
      context: SerializationContext,
      model: OrderReview,
      map: SerializedData
  ): SerializedData {
    let version = model.$schemaVersion.to;

    if(version >= 5) {
      version = 4;

      map['appointmentId'] ??= map['orderId'];
      map['appointmentDate'] ??= map['orderDate'];
      map['clientId'] ??= map['reviewerId'];
      map['clientFirstName'] ??= map['reviewerFirstName'];
      map['clientLastName'] ??= map['reviewerLastName'];
      map['clientPhoto'] ??= map['reviewerPhoto'];
      map['likes'] ??= 0;
      map['dislikes'] ??= 0;
      map['$schema_version'] = VersionRange.encodedBetween(1, 5);
    }

    return map;
  }
}
